import React, { FC, Fragment, useMemo } from "react"
import { Link } from "react-router-dom"

import { Alert, Grid, Text } from "@mantine/core"

import Page from "../components/layouts/page/Page"
import DeviceSummaryCard from "../features/devices/components/device-summary-card/DeviceSummaryCard"
import { DeviceDocument, useDevices, useGroups } from "../contexts/devices-context"
import PageDivider from "../components/layouts/page/PageDivider"
import { useLanguage } from "../contexts/language-context"
import DevicesFilter from "../features/devices/components/devices-filter/DevicesFilter"
import useDevicesFilter from "../features/devices/hooks/useDevicesFilter"
import { IconRouterOff } from "@tabler/icons-react"

const Devices: FC = () => {
    const { L } = useLanguage()
    const { data: devices, active, pending } = useDevices()
    const { map: groups } = useGroups()
    const { filter, filtered, setModulesFilter, setGroupFilter, setStatusFilter, setNameFilter, resetFilter } =
        useDevicesFilter()
    const devicesPerGroup = useMemo(
        () =>
            filtered.reduce<Record<string, DeviceDocument[]>>(
                (acc, curr) => ({
                    ...acc,
                    [curr.group]: curr.group in acc ? [...acc[curr.group], curr] : [curr],
                }),
                {},
            ),
        [filtered],
    )

    return (
        <Page
            title={L("Devices")}
            subtitle={L("%s/%s active", active.length, devices.length)}
            actions={[
                [
                    <DevicesFilter
                        filter={filter}
                        setNameFilter={setNameFilter}
                        setGroupFilter={setGroupFilter}
                        setStatusFilter={setStatusFilter}
                        setModulesFilter={setModulesFilter}
                        resetFilter={resetFilter}
                    />,
                ],
            ]}
        >
            <Grid gutter="xl" mt="2rem">
                {!!pending.length && (
                    <Grid.Col span={12}>
                        <Alert
                            title={
                                pending.length === 1
                                    ? L("%s has pending or unknown status", pending[0].name)
                                    : pending.length === 2
                                    ? L("%s and %s have pending or unknown status", pending[0].name, pending[1].name)
                                    : L(
                                          "%s, %s and %s other devices have pending or unknown status",
                                          pending[0].name,
                                          pending[1].name,
                                          pending.length - 2,
                                      )
                            }
                            color="red"
                            variant="outline"
                            icon={<IconRouterOff size={20} />}
                        >
                            {L(
                                "We haven't heard from these devices in a while. This could be due to network issues or an empty battery.",
                            )}
                        </Alert>
                    </Grid.Col>
                )}
                {Object.entries(devicesPerGroup).map(([group, devicesInGroup]) => (
                    <Fragment key={group}>
                        <Grid.Col span={12}>
                            <PageDivider>
                                {group in groups ? (
                                    <Text
                                        sx={{ "&:hover": { textDecoration: "underline" } }}
                                        component={Link}
                                        to={`/groups/${group}`}
                                        inherit
                                    >
                                        {groups[group].name} ({devicesInGroup.length})
                                    </Text>
                                ) : (
                                    L("Without group")
                                )}
                            </PageDivider>
                        </Grid.Col>
                        {devicesInGroup.map(device => (
                            <Grid.Col span={12} sm={6} lg={4} key={device.id}>
                                <DeviceSummaryCard device={device} />
                            </Grid.Col>
                        ))}
                    </Fragment>
                ))}
            </Grid>
        </Page>
    )
}

export default Devices
